import React from 'react';
import { Divider } from '@mui/material';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'grid',
        gridTemplateRows: '1fr',
        width: '100%',
        height: '100%',
        minHeight: 0,
        position: 'relative',
    },
    rootWithSidebar: {
        gridTemplateColumns: '1fr auto auto',
    },
    rootWithoutSidebar: {
        gridTemplateColumns: '1fr',
    },
    leftPane: {
        minWidth: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: '100%',
    },
    leftPaneSolo: {
        minWidth: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: '100%',
        gridRow: '1 / -1',
        gridColumn: '1 / -1',
    },
    rightPane: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    dividerContainer: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 8,
        height: '100%',
    },
    divider: {
        cursor: 'col-resize',
        userSelect: 'none',
        zIndex: 1,
        width: 8,
        backgroundColor: 'transparent',
        borderRight: 'none',
        '&.MuiDivider-vertical': {
            width: 8,
            borderRight: 'none',
        },
    },
    handleLine: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 1,
        height: 30,
        backgroundColor: theme.palette.divider,
        zIndex: 2,
        pointerEvents: 'none',
        borderRadius: 1,
    },
    handleOverlay: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        width: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        cursor: 'col-resize',
        zIndex: 3,
    },
}));

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Reusable resizable layout with a right sidebar.
 * Props:
 * - left: ReactNode
 * - right: ReactNode
 * - showRight: boolean
 * - storageKey: string | null
 * - initialRightPx: number (default 420)
 * - minRightPx: number (default 280)
 * - maxRightPx: number (default 880)
 */
const ResizableSidebar = ({
    left,
    right,
    showRight = false,
    storageKey = null,
    initialRightPx = 420,
    minRightPx = 280,
    maxRightPx = 880,
}) => {
    const classes = useStyles();
    const containerRef = React.useRef(null);
    const [rightWidth, setRightWidth] = React.useState(initialRightPx);
    const startDragRef = React.useRef({ dragging: false, startX: 0, startWidth: initialRightPx });

    React.useEffect(() => {
        if (!storageKey) return;
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                const parsed = parseInt(saved, 10);
                if (!Number.isNaN(parsed)) {
                    setRightWidth(clamp(parsed, minRightPx, maxRightPx));
                }
            }
        } catch (_) {
            // ignore
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageKey]);

    React.useEffect(() => {
        if (!storageKey) return;
        try {
            localStorage.setItem(storageKey, String(rightWidth));
        } catch (_) {
            // ignore
        }
    }, [rightWidth, storageKey]);

    const onMouseMove = React.useCallback((ev) => {
        if (!startDragRef.current.dragging || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const containerRight = rect.left + rect.width;
        const newWidth = containerRight - ev.clientX;
        setRightWidth((prev) => clamp(newWidth, minRightPx, maxRightPx));
    }, [minRightPx, maxRightPx]);

    const stopDragging = React.useCallback(() => {
        startDragRef.current.dragging = false;
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', stopDragging);
    }, [onMouseMove]);

    const startDragging = React.useCallback((ev) => {
        ev.preventDefault();
        startDragRef.current.dragging = true;
        startDragRef.current.startX = ev.clientX;
        startDragRef.current.startWidth = rightWidth;
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', stopDragging);
    }, [onMouseMove, stopDragging, rightWidth]);

    React.useEffect(() => () => stopDragging(), [stopDragging]);

    const renderDivider = () => (
        <div className={classes.dividerContainer}>
            <Divider orientation="vertical" flexItem className={classes.divider} />
            <div className={classes.handleLine} />
            <div className={classes.handleOverlay} onMouseDown={startDragging} />
        </div>
    );

    if (!showRight) {
        return (
            <div ref={containerRef} className={`${classes.root} ${classes.rootWithoutSidebar}`}>
                <div className={classes.leftPaneSolo}>{left}</div>
            </div>
        );
    }

    return (
        <div ref={containerRef} className={`${classes.root} ${classes.rootWithSidebar}`}>
            <div className={classes.leftPane}>{left}</div>
            {renderDivider()}
            <div className={classes.rightPane} style={{ width: rightWidth }}>{right}</div>
        </div>
    );
};

export default ResizableSidebar;


